import React, { useMemo, useState } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import GeneralSection from './event/GeneralSection';
import DetailsSection from './event/DetailsSection';
import RecurrenceSection from './event/RecurrenceSection';
import PricingSection from './event/PricingSection';
import DescriptionSection from './event/DescriptionSection';
import RecurringScopeDialog from './RecurringScopeDialog';
import ConflictDialog from './ConflictDialog';
import useEventForm from '../../hooks/useEventForm';
import { useStudents } from '../../data/useStudents';
import { useCategories } from '../../data/useCategories';
import { useSettings } from '../../data/useSettings';
import { useAuth } from '../../context/AuthContext';
import useNames from '../../hooks/useNames';
import { eventShape, EDIT_SHAPE, EVENT_MODES } from '../../domain/eventModes';

// New / edit calendar event, following the MMS form (SPEC.md §2.1).
//
// The section order is theirs and worth keeping: visibility and booking options
// first, then who and when, then how it repeats, then how it is priced.
//
// Assembly only — the form state and the three save paths are useEventForm, and
// each section renders itself.
//
// `initialTime` and `initialDuration` come from the slot that was clicked or
// dragged; entry points with no particular time (the Add Event menu, the
// empty state) fall back to the defaults.
//
// The form is built in a lazy initialiser that runs once per mount, so the
// caller must mount this only while it is open.
export default function EventDrawer({
  open, onClose, initialDate, initialTime, initialDuration,
  mode = EVENT_MODES.full, editing = null,
}) {
  // Editing always shows the full form: the event already exists and may carry
  // values a narrower form cannot display, and a hidden field holding a value is
  // one that gets silently dropped on the next save.
  const shape = editing ? EDIT_SHAPE : eventShape(mode);
  const { students, loading: studentsLoading } = useStudents();
  const { categories, loaded, locations } = useCategories();
  const [expanded, setExpanded] = useState(false);

  const {
    form, set, canSave, saving, scopeOpen, setScopeOpen, save, saveWithScope, chooseCategory,
    recurrenceChanged, deletesEntriesAfter, conflict, confirmConflict, cancelConflict,
    weeklyHoldDays, priceError, intervalError, maxStudentsError,
  } = useEventForm({
    editing, initialDate, initialTime, initialDuration, shape, categories, loaded,
    // The raw rows, not `named` below: the hook reads `durationMin` off them to
    // seed a new lesson's duration from the student attending it (#241).
    students,
    onDone: onClose,
  });

  const names = useNames();
  // By the Student Name Format's sort key, as the Students table, Package
  // Balances and Android's attendee picker sort them. The provider's order is
  // Firestore's `orderBy('lastName')`, code-point order: `de Souza` after
  // `Zhou`, and the format ignored.
  const named = useMemo(
    () => students
      .map((s) => ({ ...s, name: names.student(s), sortKey: names.studentSortKey(s) }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey)),
    [students, names]
  );

  // Who the conflict warning names. MMS's line is about a person being in two
  // places — "<Teacher Name> is already attending another event at this time"
  // — and on an event with no attendees it names the teacher, who attends
  // everything on their own calendar. The teacher's name as First Last — MMS
  // applies no Teacher Name Format in the event drawer (#797) — from the
  // stored pair or the sign-in's display name, else the studio's name; with
  // none, the absence is passed on and `ConflictDialog` has a sentence for it
  // (#385).
  const { user } = useAuth();
  const { settings } = useSettings();
  const teacherName = names.teacherFullName(user?.displayName) || settings.name || '';
  const studentName = (id) => named.find((st) => st.id === id)?.name || null;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={shape.title}
        expanded={expanded}
        onToggleExpand={() => setExpanded((e) => !e)}
        footer={(
          <>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <FooterSpacer />
            <Button onClick={save} loading={saving} disabled={!canSave}>Save</Button>
          </>
        )}
      >
        <GeneralSection form={form} set={set} shape={shape} maxStudentsError={maxStudentsError} />
        <DetailsSection
          form={form}
          set={set}
          students={named}
          studentsRead={!studentsLoading}
          categories={categories}
          locations={locations}
          onCategoryChange={chooseCategory}
          shape={shape}
        />
        <RecurrenceSection
          form={form}
          set={set}
          shape={shape}
          deletesEntriesAfter={deletesEntriesAfter}
          weeklyHoldDays={weeklyHoldDays}
          intervalError={intervalError}
        />
        {shape.pricing ? <PricingSection form={form} set={set} priceError={priceError} /> : null}
        {shape.publicDescription ? <DescriptionSection form={form} set={set} shape={shape} /> : null}
      </Drawer>

      {/* Two different questions behind one Save. A per-occurrence edit asks
          which entries it applies to; a recurrence change has no such choice,
          so MMS states what it will do and asks only whether to do it. */}
      <RecurringScopeDialog
        open={scopeOpen}
        action={recurrenceChanged ? 'recurrence' : 'update'}
        scopes={!recurrenceChanged}
        onCancel={() => setScopeOpen(false)}
        onConfirm={saveWithScope}
      />

      {/* Between choosing to save and the save happening. Nothing has been
          written when this is up, so Cancel really does undo the change — the
          drawer simply stays open with the edits still in it. */}
      <ConflictDialog
        open={Boolean(conflict)}
        result={conflict}
        teacherName={teacherName}
        studentName={studentName}
        onCancel={cancelConflict}
        onConfirm={confirmConflict}
      />
    </>
  );
}
